/* eslint-disable react-refresh/only-export-components -- router config module:
   the lazy() route consts are not Fast-Refresh component exports, and this file
   intentionally only exports the `router` instance. */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { env } from '@/env';
import { AppShell } from '@/components/layout/AppShell';
import { Loader } from '@/components/common/Loader';
import { RoleGate } from '@/components/common/RoleGate';
import { LIMULO_PATHS } from '@/lib/limulo/routes';
import { AuthCallback } from './AuthCallback';
import { Hub } from './Hub';
import { Tournaments } from './Tournaments';
import { TournamentDetail } from './TournamentDetail';
import { WatchParty } from './WatchParty';
import { MatchDetail } from './MatchDetail';
import { TeamDetail } from './TeamDetail';
import { ChatList } from './ChatList';
import { ChatRoom } from './ChatRoom';
import { Profile } from './Profile';
import { UserProfile } from './UserProfile';
import { Leaderboards } from './Leaderboards';
import { Inbox } from './Inbox';
import { League } from './League';
import { LeaderboardDetail } from './LeaderboardDetail';
import { News } from './News';
import { ArticleDetail } from './ArticleDetail';
import { NewsTag } from './NewsTag';
import { NewsPreferences } from './NewsPreferences';
import { Preferences } from './settings/Preferences';
import { SettingsHome } from './settings/SettingsHome';
import { Appearance } from './settings/Appearance';
import { LinkedAccounts } from './settings/LinkedAccounts';
import { WalletLevels } from './settings/WalletLevels';
import { BannerSettings } from './settings/BannerSettings';
import { Communication } from './settings/Communication';
import { Friends } from './Friends';
import { DuelLink } from './duels/DuelLink';
import { InviteLanding } from './InviteLanding';

// DuelResult is lazy: it pulls the QBot card catalog + saved-deck + social
// surfaces, only reached after a completed duel — keep it out of the main bundle.
const DuelResult = lazy(() => import('./duels/DuelResult').then((m) => ({ default: m.DuelResult })));
const DuelTournamentDetail = lazy(() =>
  import('./duels/DuelTournamentDetail').then((m) => ({ default: m.DuelTournamentDetail })),
);

// ── Code-split route trees ────────────────────────────────────────────────
// Avatar, Riot and Clash Royale screens are lazy so their component code
// lands in dedicated chunks (see vite.config.ts manualChunks). Combined with
// the game-conditional Hub, a Clash Royale player never navigates toward the
// Riot tree and so never downloads its chunk. The RTK Query slices themselves
// (qbotApi/qriotsApi/avatarApi) stay in the main bundle for now because the
// store imports them eagerly — disaccoppiamento is a follow-up.

// Avatars
const Avatars = lazy(() => import('./Avatars').then((m) => ({ default: m.Avatars })));
const AvatarCreate = lazy(() => import('./AvatarCreate').then((m) => ({ default: m.AvatarCreate })));
const AvatarDetail = lazy(() => import('./AvatarDetail').then((m) => ({ default: m.AvatarDetail })));
const AvatarEdit = lazy(() => import('./AvatarEdit').then((m) => ({ default: m.AvatarEdit })));
const AvatarSeries = lazy(() => import('./AvatarSeries').then((m) => ({ default: m.AvatarSeries })));
const AvatarClipDetail = lazy(() =>
  import('./AvatarClipDetail').then((m) => ({ default: m.AvatarClipDetail })),
);
const AvatarEditor = lazy(() =>
  import('./avatar-editor/AvatarEditor').then((m) => ({ default: m.AvatarEditor })),
);
const AvatarAssistant = lazy(() =>
  import('./avatar-assistant/AvatarAssistant').then((m) => ({ default: m.AvatarAssistant })),
);

// Riot
const Riot = lazy(() => import('./Riot').then((m) => ({ default: m.Riot })));
const RiotLolDetail = lazy(() => import('./RiotLolDetail').then((m) => ({ default: m.RiotLolDetail })));
const RiotLolMastery = lazy(() =>
  import('./RiotLolMastery').then((m) => ({ default: m.RiotLolMastery })),
);
const RiotLolMatches = lazy(() =>
  import('./RiotLolMatches').then((m) => ({ default: m.RiotLolMatches })),
);
const RiotLolStats = lazy(() => import('./RiotLolStats').then((m) => ({ default: m.RiotLolStats })));
const RiotMultiSearch = lazy(() =>
  import('./RiotMultiSearch').then((m) => ({ default: m.RiotMultiSearch })),
);
const RiotCompare = lazy(() =>
  import('./RiotCompare').then((m) => ({ default: m.RiotCompare })),
);
const RiotSaved = lazy(() => import('./RiotSaved').then((m) => ({ default: m.RiotSaved })));
const RiotTftDetail = lazy(() => import('./RiotTftDetail').then((m) => ({ default: m.RiotTftDetail })));

// Clash Royale
const CRDecks = lazy(() => import('./cr/CRDecks').then((m) => ({ default: m.CRDecks })));
const CRLink = lazy(() => import('./cr/CRLink').then((m) => ({ default: m.CRLink })));
const CRSavedDecks = lazy(() => import('./cr/CRSavedDecks').then((m) => ({ default: m.CRSavedDecks })));
const CRPopularDecks = lazy(() =>
  import('./cr/CRPopularDecks').then((m) => ({ default: m.CRPopularDecks })),
);
const CRBattles = lazy(() => import('./cr/CRBattles').then((m) => ({ default: m.CRBattles })));
const CRBattleDetail = lazy(() =>
  import('./cr/CRBattleDetail').then((m) => ({ default: m.CRBattleDetail })),
);
const CRPlayerProfile = lazy(() =>
  import('./cr/CRPlayerProfile').then((m) => ({ default: m.CRPlayerProfile })),
);
const CRDeckCheck = lazy(() => import('./cr/CRDeckCheck').then((m) => ({ default: m.CRDeckCheck })));
const CRDeckBuilder = lazy(() =>
  import('./cr/CRDeckBuilder').then((m) => ({ default: m.CRDeckBuilder })),
);

// QChess — chess tournaments + duels on Lichess + QLASH leaderboard.
const Chess = lazy(() => import('./Chess').then((m) => ({ default: m.Chess })));
const ChessTournaments = lazy(() =>
  import('./ChessTournaments').then((m) => ({ default: m.ChessTournaments })),
);
const ChessTournamentDetail = lazy(() =>
  import('./ChessTournamentDetail').then((m) => ({ default: m.ChessTournamentDetail })),
);
const ChessChallenges = lazy(() =>
  import('./ChessChallenges').then((m) => ({ default: m.ChessChallenges })),
);
const ChessChallengeDetail = lazy(() =>
  import('./ChessChallengeDetail').then((m) => ({ default: m.ChessChallengeDetail })),
);
const ChessLeaderboard = lazy(() =>
  import('./ChessLeaderboard').then((m) => ({ default: m.ChessLeaderboard })),
);
const ChessBrackets = lazy(() =>
  import('./ChessBrackets').then((m) => ({ default: m.ChessBrackets })),
);
const ChessBracketDetail = lazy(() =>
  import('./ChessBracketDetail').then((m) => ({ default: m.ChessBracketDetail })),
);

// QClans — Clash of Clans stats / war tracking / competitions / AI coaching.
const CocHome = lazy(() => import('./coc/CocHome').then((m) => ({ default: m.CocHome })));
const CocLink = lazy(() => import('./coc/CocLink').then((m) => ({ default: m.CocLink })));
const CocProfile = lazy(() => import('./coc/CocProfile').then((m) => ({ default: m.CocProfile })));
const CocClan = lazy(() => import('./coc/CocClan').then((m) => ({ default: m.CocClan })));
const CocCompetitions = lazy(() =>
  import('./coc/CocCompetitions').then((m) => ({ default: m.CocCompetitions })),
);
const CocCompetitionDetail = lazy(() =>
  import('./coc/CocCompetitionDetail').then((m) => ({ default: m.CocCompetitionDetail })),
);
const CocCoach = lazy(() => import('./coc/CocCoach').then((m) => ({ default: m.CocCoach })));

// QGammon — real-time multiplayer backgammon (leaderboard + live play).
const BackgammonHome = lazy(() =>
  import('./backgammon/BackgammonHome').then((m) => ({ default: m.BackgammonHome })),
);
const Missions = lazy(() =>
  import('./missioni/Missions').then((m) => ({ default: m.Missions })),
);
const BackgammonMatch = lazy(() =>
  import('./backgammon/BackgammonMatch').then((m) => ({ default: m.BackgammonMatch })),
);
const BackgammonReplay = lazy(() =>
  import('./backgammon/BackgammonReplay').then((m) => ({ default: m.BackgammonReplay })),
);

// QChinesePoker (QCP) — Pineapple OFC + Fantasyland live match.
// Home (T6) and result (T11) routes are added by those tasks.
const ChinesePokerHome = lazy(() =>
  import('./chinesepoker/ChinesePokerHome').then((m) => ({ default: m.ChinesePokerHome })),
);
const ChinesePokerMatch = lazy(() =>
  import('./chinesepoker/ChinesePokerMatch').then((m) => ({ default: m.ChinesePokerMatch })),
);
// T11: result screen — card.png + win/loss header + Telegram share.
const ChinesePokerResult = lazy(() =>
  import('./chinesepoker/ChinesePokerResult').then((m) => ({ default: m.ChinesePokerResult })),
);
// T6: per-hand replay viewer — final boards + discards + net per hand.
const ChinesePokerReplay = lazy(() =>
  import('./chinesepoker/ChinesePokerReplay').then((m) => ({ default: m.ChinesePokerReplay })),
);

/** Wrap a lazy route element in a Suspense boundary with the app Loader. */
function lazyRoute(node: ReactNode): ReactNode {
  return <Suspense fallback={<Loader />}>{node}</Suspense>;
}

// Routes gated by env feature flags. When the corresponding feature is
// disabled (PROD), the route entry is omitted from the children array — a
// direct URL like `/avatars`, `/riot`, or `/cr` falls through to the `*`
// catch-all and redirects to `/`. Filtering at module load is fine: env
// values are frozen at build time, so the router never needs to re-evaluate.
const avatarRoutes: RouteObject[] = env.featureAvatarsEnabled
  ? [
      { path: 'avatars', element: lazyRoute(<Avatars />) },
      { path: 'avatars/new', element: lazyRoute(<AvatarCreate />) },
      { path: 'avatars/new/assistant', element: lazyRoute(<AvatarAssistant />) },
      { path: 'avatars/:id', element: lazyRoute(<AvatarDetail />) },
      { path: 'avatars/:id/edit', element: lazyRoute(<AvatarEdit />) },
      { path: 'avatars/:id/series', element: lazyRoute(<AvatarSeries />) },
      { path: 'avatars/:id/clips/:clipId', element: lazyRoute(<AvatarClipDetail />) },
    ]
  : [];

const riotRoutes: RouteObject[] = env.featureQriotsEnabled
  ? [
      { path: 'riot', element: lazyRoute(<Riot />) },
      { path: 'riot/saved', element: lazyRoute(<RiotSaved />) },
      { path: 'riot/multi-search', element: lazyRoute(<RiotMultiSearch />) },
      { path: 'riot/compare', element: lazyRoute(<RiotCompare />) },
      { path: 'riot/lol/:puuid', element: lazyRoute(<RiotLolDetail />) },
      { path: 'riot/lol/:puuid/matches', element: lazyRoute(<RiotLolMatches />) },
      { path: 'riot/lol/:puuid/stats', element: lazyRoute(<RiotLolStats />) },
      { path: 'riot/lol/:puuid/mastery', element: lazyRoute(<RiotLolMastery />) },
      { path: 'riot/tft/:puuid', element: lazyRoute(<RiotTftDetail />) },
    ]
  : [];

const crRoutes: RouteObject[] = env.featureClashRoyaleEnabled
  ? [
      // /cr is now a redirect to the Mazzi landing (compat with old links /
      // the CRHubTile). The deck-focused landing is /cr/decks.
      { path: 'cr', element: <Navigate to="/cr/decks" replace /> },
      { path: 'cr/decks', element: lazyRoute(<CRDecks />) },
      { path: 'cr/link', element: lazyRoute(<CRLink />) },
      { path: 'cr/decks/saved', element: lazyRoute(<CRSavedDecks />) },
      { path: 'cr/decks/popular', element: lazyRoute(<CRPopularDecks />) },
      { path: 'cr/decks/check', element: lazyRoute(<CRDeckCheck />) },
      { path: 'cr/decks/builder', element: lazyRoute(<CRDeckBuilder />) },
      { path: 'cr/battles', element: lazyRoute(<CRBattles />) },
      { path: 'cr/battles/:idx', element: lazyRoute(<CRBattleDetail />) },
      { path: 'cr/player/:tag', element: lazyRoute(<CRPlayerProfile />) },
    ]
  : [];

const chessRoutes: RouteObject[] = env.featureChessEnabled
  ? [
      { path: 'chess', element: lazyRoute(<Chess />) },
      { path: 'chess/tournaments', element: lazyRoute(<ChessTournaments />) },
      { path: 'chess/tournaments/:id', element: lazyRoute(<ChessTournamentDetail />) },
      { path: 'chess/brackets', element: lazyRoute(<ChessBrackets />) },
      { path: 'chess/brackets/:id', element: lazyRoute(<ChessBracketDetail />) },
      { path: 'chess/challenges', element: lazyRoute(<ChessChallenges />) },
      { path: 'chess/challenges/:id', element: lazyRoute(<ChessChallengeDetail />) },
      { path: 'chess/leaderboard', element: lazyRoute(<ChessLeaderboard />) },
    ]
  : [];

const cocRoutes: RouteObject[] = env.featureCocEnabled
  ? [
      { path: 'coc', element: lazyRoute(<CocHome />) },
      { path: 'coc/link', element: lazyRoute(<CocLink />) },
      { path: 'coc/profile', element: lazyRoute(<CocProfile />) },
      { path: 'coc/clan', element: lazyRoute(<CocClan />) },
      { path: 'coc/competitions', element: lazyRoute(<CocCompetitions />) },
      { path: 'coc/competitions/:id', element: lazyRoute(<CocCompetitionDetail />) },
      { path: 'coc/coach', element: lazyRoute(<CocCoach />) },
    ]
  : [];

// Sotto-sezione Missioni (Quest) — dietro `featureMissionsEnabled`, OPT-IN: nasce
// spenta ovunque, PROD compresa. UNA rotta e basta: qui c'e' la sola lista, il
// claim e' fase 2 e non ha una schermata propria.
//
// ⚠️ STA SOTTO `settings/`, e la vecchia `/missioni` di primo livello NON resta
// accanto: e' una sotto-sezione del blocco IMPOSTAZIONI del Profilo (ordine di
// Luca, 2026-08-30). Due porte per la stessa cosa e' esattamente il problema che
// il consolidamento del 2026-07-24 ha chiuso — `/settings` stesso e' un redirect
// al Profilo proprio per non averne due.
//
// Il nome dice COSA contiene, non quale servizio la serve: le vicine sono
// `settings/wallet` (servita da QBank) e `settings/accounts`, non
// `settings/qbank`. E `missions`, mai `challenges`: in questo repo `challenges`
// significa gia' le Sfide di QDuels (`locales/*/challenges.json`).
// ── Limulo ────────────────────────────────────────────────────────────────
// La sezione servita dal BFF `app-qlash-api` (ADR-0078). Lazy come le altre: chi non
// ha il flag e il ruolo non ne scarica nemmeno il codice.
const LimuloTournaments = lazy(() =>
  import('./limulo/LimuloTournaments').then((m) => ({ default: m.LimuloTournaments })),
);
const LimuloTournamentDetail = lazy(() =>
  import('./limulo/LimuloTournamentDetail').then((m) => ({ default: m.LimuloTournamentDetail })),
);
const LimuloStandings = lazy(() =>
  import('./limulo/LimuloStandings').then((m) => ({ default: m.LimuloStandings })),
);
const LimuloMine = lazy(() =>
  import('./limulo/LimuloMine').then((m) => ({ default: m.LimuloMine })),
);
const LimuloDiagnostics = lazy(() =>
  import('./limulo/LimuloDiagnostics').then((m) => ({ default: m.LimuloDiagnostics })),
);
const LimuloMatches = lazy(() =>
  import('./limulo/LimuloMatches').then((m) => ({ default: m.LimuloMatches })),
);
const LimuloMatchDetail = lazy(() =>
  import('./limulo/LimuloMatchDetail').then((m) => ({ default: m.LimuloMatchDetail })),
);
const LimuloProfiles = lazy(() =>
  import('./limulo/LimuloProfiles').then((m) => ({ default: m.LimuloProfiles })),
);
const LimuloProfileCreate = lazy(() =>
  import('./limulo/LimuloProfileCreate').then((m) => ({ default: m.LimuloProfileCreate })),
);

/**
 * Sezione Limulo — DUE serrature indipendenti, e servono entrambe.
 *
 *  1. `env.featureLimuloEnabled` e' compile-time e OPT-IN (`=== 'true'`): senza, queste
 *     rotte non esistono proprio. Deve essere opt-in perche' oggi ogni rotta dati del BFF
 *     risponde 403 — accesa di default, la sezione comparirebbe rotta ovunque.
 *  2. `<RoleGate role="beta">` e' feature-level e gira a runtime sul ruolo Logto.
 *
 * ⚠️ I PATH STANNO TUTTI SOTTO `limulo/`, e non e' una preferenza estetica.
 * `createBrowserRouter` risolve per RANK di specificita', non per ordine di dichiarazione:
 * mettere queste rotte in fondo all'array non protegge da niente. Davanti a un path
 * DUPLICATO ESATTO vince chi e' dichiarato prima — senza errori, senza warning, con la
 * build verde. E su questo router vivono gia' `tournaments`, `tournaments/:id`,
 * `tournaments/:id/watch-party` e `teams/:id`, che sono la superficie tornei del monolite
 * Laravel ATTIVA in produzione: una rotta nuda le metterebbe in ombra, e siccome le nostre
 * stanno dietro il RoleGate l'ombra la vedrebbero SOLO gli utenti beta.
 *
 * I path vengono da `lib/limulo/routes.ts`, dove `tools/verify-limulo.ts` verifica il
 * prefisso: la regola e' meccanica invece che ricordata.
 */
const limuloRoutes: RouteObject[] = env.featureLimuloEnabled
  ? [
      {
        path: LIMULO_PATHS.tournaments,
        element: <RoleGate role="beta">{lazyRoute(<LimuloTournaments />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.tournamentDetail,
        element: <RoleGate role="beta">{lazyRoute(<LimuloTournamentDetail />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.standings,
        element: <RoleGate role="beta">{lazyRoute(<LimuloStandings />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.mine,
        element: <RoleGate role="beta">{lazyRoute(<LimuloMine />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.diagnostics,
        element: <RoleGate role="beta">{lazyRoute(<LimuloDiagnostics />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.matches,
        element: <RoleGate role="beta">{lazyRoute(<LimuloMatches />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.matchDetail,
        element: <RoleGate role="beta">{lazyRoute(<LimuloMatchDetail />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.profiles,
        element: <RoleGate role="beta">{lazyRoute(<LimuloProfiles />)}</RoleGate>,
      },
      {
        path: LIMULO_PATHS.profileCreate,
        element: <RoleGate role="beta">{lazyRoute(<LimuloProfileCreate />)}</RoleGate>,
      },
    ]
  : [];

const missionsRoutes: RouteObject[] = env.featureMissionsEnabled
  ? [{ path: 'settings/missions', element: lazyRoute(<Missions />) }]
  : [];

// Backgammon — DUE serrature, e servono entrambe. `featureBackgammonEnabled` e'
// compile-time: senza, queste rotte non esistono. `<RoleGate role="beta">` e'
// feature-level e gira a runtime sul ruolo Logto.
//
// ⚠️ Il flag da solo NON basta a limitare la sezione ai beta, ed e' il motivo per
// cui questo gate esiste. Il flag e' GLOBALE: accenderlo la rendeva raggiungibile
// da chiunque, e un utente col ruolo `user` porta ZERO scope sulla resource
// QGammon — quindi la sezione gli si sarebbe rotta in mano, senza nemmeno il
// dev-bypass a coprire (in produzione e' una stringa vuota letterale, per
// costruzione). Il gate va su entrambe le vie d'accesso: qui e sull'ingresso
// nell'Hub (`components/hub/HubSections.tsx`) — una rotta protetta con una tile
// visibile e' un vicolo cieco, non una protezione.
//
// Stessa forma della sezione Limulo piu' sotto: nessun `fallback`, quindi un
// utente non-beta che arriva qui per deep-link vede un'area vuota. E' il
// comportamento del precedente, non una scelta nuova — se si vuole un rimando
// alla Home e' una decisione da prendere per ENTRAMBE le sezioni insieme.
const backgammonRoutes: RouteObject[] = env.featureBackgammonEnabled
  ? [
      {
        path: 'backgammon',
        element: <RoleGate role="beta">{lazyRoute(<BackgammonHome />)}</RoleGate>,
      },
      {
        path: 'backgammon/match/:id',
        element: <RoleGate role="beta">{lazyRoute(<BackgammonMatch />)}</RoleGate>,
      },
      {
        path: 'backgammon/replay/:id',
        element: <RoleGate role="beta">{lazyRoute(<BackgammonReplay />)}</RoleGate>,
      },
    ]
  : [];

// QChinesePoker routes — gated by featureChinesePokerEnabled (PROD sets false
// until the game ships publicly). T6 adds the home route; T11 adds the result
// route. Each task appends to this array so the router stays in one place.
const chinesePokerRoutes: RouteObject[] = env.featureChinesePokerEnabled
  ? [
      { path: 'chinesepoker', element: lazyRoute(<ChinesePokerHome />) },
      { path: 'chinesepoker/match/:id', element: lazyRoute(<ChinesePokerMatch />) },
      // T11: result screen — card PNG + win/loss header + Telegram share.
      { path: 'chinesepoker/result/:id', element: lazyRoute(<ChinesePokerResult />) },
      // T6: per-hand replay viewer.
      { path: 'chinesepoker/replay/:id', element: lazyRoute(<ChinesePokerReplay />) },
    ]
  : [];

export const router = createBrowserRouter([
  // Public route: must NOT be wrapped in AppShell (no auth gating, no TabBar)
  // — AppShell would loop the user back to <Login /> before the OIDC code is
  // exchanged for tokens.
  { path: '/auth/callback', element: <AuthCallback /> },

  // Avatar Editor wizard runs full-screen — bypass AppShell so the TabBar
  // doesn't intercept clicks on the sticky footer Next/Back buttons.
  ...(env.featureAvatarsEnabled
    ? [{ path: '/avatars/editor', element: lazyRoute(<AvatarEditor />) }]
    : []),

  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Hub /> },

      { path: 'tournaments', element: <Tournaments /> },
      { path: 'tournaments/:id', element: <TournamentDetail /> },
      { path: 'tournaments/:id/watch-party', element: <WatchParty /> },

      { path: 'matches/:id', element: <MatchDetail /> },
      { path: 'teams/:id', element: <TeamDetail /> },

      { path: 'chat', element: <ChatList /> },
      { path: 'chat/:id', element: <ChatRoom /> },

      { path: 'profile', element: <Profile /> },
      { path: 'profile/:id', element: <UserProfile /> },

      { path: 'inbox', element: <Inbox /> },

      // League page gated by its own flag (split from the qbank wallet flag) so
      // PROD can keep the wallet beta on while the league stays held. When off,
      // `/league` falls through to the `*` catch-all → redirect to `/`.
      ...(env.featureLeagueEnabled ? [{ path: 'league', element: <League /> }] : []),

      { path: 'friends', element: <Friends /> },

      // Clash Royale account linking for 1v1 duels (qduels verify-token).
      // Prerequisite for the friends-list "Sfida" button — reached from
      // Profile → Linked accounts and from the gated Sfida affordance.
      { path: 'duels/link', element: <DuelLink /> },

      // Post-match duel result — outcome + both decks + opponent social/deck
      // actions. Reached from the QueueSheet (queue completion) and the Sfide
      // tab (tapping a completed challenge). Optional `?challengeId=` adds the
      // series X–Y header.
      { path: 'duels/result/:gameTag', element: lazyRoute(<DuelResult />) },

      // QDuels tournament detail — header, own-match state, current-round
      // bracket, roster, join/leave. Flag-gated like the lists that link here,
      // but note the reads themselves are NOT caller-scoped server-side: any
      // authenticated user can open any tournament by id, so a shared link
      // renders even for someone who never joined.
      ...(env.featureQduelsTournamentsEnabled
        ? [{ path: 'duels/tournaments/:tournamentId', element: lazyRoute(<DuelTournamentDetail />) }]
        : []),

      // Legacy leaderboard, gated like every other game surface. When off,
      // both fall through to the `*` catch-all → redirect to `/`.
      ...(env.featureLeaderboardEnabled
        ? [
            { path: 'leaderboards', element: <Leaderboards /> },
            { path: 'leaderboards/:id', element: <LeaderboardDetail /> },
          ]
        : []),

      { path: 'news', element: <News /> },
      { path: 'news/preferences', element: <NewsPreferences /> },
      { path: 'news/tag/:slug', element: <NewsTag /> },
      { path: 'news/:slug', element: <ArticleDetail /> },

      ...avatarRoutes,
      ...riotRoutes,
      ...crRoutes,
      ...chessRoutes,
      ...cocRoutes,
      // 🔑 `/invito/<codice>` sta QUI e non in `backgammonRoutes`: quelle
      // esistono solo col flag acceso e sono dentro <RoleGate role="beta">, e un
      // ospite senza ruolo cadrebbe sul catch-all `*` finendo sulla Hub — il
      // fallimento silenzioso che il deep link esiste per togliere. Questa
      // schermata c'e' sempre e dice quale dei casi e'. Vedi InviteLanding.tsx.
      { path: 'invito/:code', element: lazyRoute(<InviteLanding />) },
      ...backgammonRoutes,
      ...missionsRoutes,
      ...chinesePokerRoutes,
      ...limuloRoutes,

      { path: 'settings', element: <SettingsHome /> },
      { path: 'settings/preferences', element: <Preferences /> },
      { path: 'settings/appearance', element: <Appearance /> },
      { path: 'settings/accounts', element: <LinkedAccounts /> },
      { path: 'settings/wallet', element: <WalletLevels /> },
      { path: 'settings/banner', element: <BannerSettings /> },
      { path: 'settings/communication', element: <Communication /> },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
